import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import MainLayout from "@/components/layouts";
import { Camera, ChevronDown, ChevronRight, ChevronUp, Edit, List, MoreHorizontal, Plus, Save, Trash2, Workflow, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/layouts/page-header/index.tsx";
import { useServices } from "./services.hooks";
import { Service } from "@/models/services.model";
import AddServiceModal from "./services.component/add-service-modal";
import { Fragment } from "react";
import AddSubServiceModal from "./services.component/add-sub-service-modal";
import DeleteServiceModal from "./services.component/del-service-modal";
import { useNavigate } from "react-router";

export default function Services() {
  const breadcrumb = [{ name: "Services", href: "#" }];
  const navigate = useNavigate();

  const {
    list,
    handleToggleService,
    openedServices,
    handleExpandAll,
    handleCollapseAll,
    isEditMode,
    handleEditMode,
    handleCancelEdit,
    handleSaveChanges,
    servicePrices,
    handlePriceChange,
    openDialog,
    setOpenDialog,
    handleCreate,
    openSubDialog,
    setOpenSubDialog,
    handleMenuService,
    handleCreateSub,
    delDialog,
    setDelDialog,
    handleDeleteServiceModal,
    handleDelete,
    openDropdownId,
    setOpenDropdownId,
  } = useServices();

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <PageHeader
        title="Services"
        subtitle="Manage your services"
        buttonText="Add Service"
        onButtonClick={() => setOpenDialog(true)}
        additionalButtons={
          isEditMode ? (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="lg" onClick={handleCancelEdit} className="uppercase rounded-lg">
                <X className="size-4 mr-2" />
                Cancel
              </Button>
              <Button variant="secondary" size="lg" onClick={handleSaveChanges} className="uppercase rounded-lg">
                <Save className="size-4 mr-2" />
                Save Changes
              </Button>
            </div>
          ) : (
            <Button variant="secondary" size="lg" onClick={handleEditMode} className="uppercase rounded-lg">
              <Edit className="size-4 mr-2" />
              Edit
            </Button>
          )
        }
      />
      <div className="container mx-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <div className="flex items-center justify-between">
                  <span>Service</span>
                  <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleExpandAll}
                          className="h-7 px-2"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Expand All</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCollapseAll}
                          className="h-7 px-2"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Collapse All</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </TableHead>
              <TableHead>Rate of Service</TableHead>
              <TableHead>Individual Payment</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((service) => (
              <Fragment key={service.id}>
                <TableRow>
                  <TableCell colSpan={3} className="bg-muted font-medium">
                    <div
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={() => handleToggleService(service.id)}
                    >
                      <ChevronRight
                        className={`h-4 w-4 transition-transform duration-300 ${
                          openedServices.includes(service.id) ? "rotate-90" : ""
                        }`}
                      />
                      {service.name}
                    </div>
                  </TableCell>
                  <TableCell className="bg-muted font-medium">
                    <DropdownMenu
                      open={openDropdownId === service.id}
                      onOpenChange={(open) => setOpenDropdownId(open ? service.id : null)}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost">
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => handleMenuService(service.id)}>
                          <Plus className="mr-2" /> Add Sub Service
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/services/surveys/" + service.id)}>
                          <Workflow className="mr-2" /> Manage Surveys
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onClick={() => handleDeleteServiceModal(service.id)}>
                          <Trash2 className="mr-2" /> Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>

                {openedServices.includes(service.id) && (
                  <>
                    {service.children.map((sub: Service, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{sub.name}</TableCell>
                        <TableCell>
                          {isEditMode ? (
                            <div className="flex items-center gap-4">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={servicePrices[sub.id]?.price ?? sub.price}
                                onChange={(e) =>
                                  handlePriceChange(sub.id, "price", parseFloat(e.target.value) || 0)
                                }
                                className="w-24"
                              />
                              <label className="flex items-center gap-2 text-sm whitespace-nowrap">
                                <Checkbox
                                  checked={servicePrices[sub.id]?.pricedPerJob ?? sub.priced_per_job}
                                  onCheckedChange={(checked) => handlePriceChange(sub.id, "pricedPerJob", checked === true)}
                                />
                                Priced per job
                              </label>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              {sub.price}
                              {sub.priced_per_job && <Badge variant="secondary">Priced per job</Badge>}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditMode ? (
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={servicePrices[sub.id]?.payment ?? sub.payment}
                              onChange={(e) =>
                                handlePriceChange(sub.id, "payment", parseFloat(e.target.value) || 0)
                              }
                              className="w-24"
                            />
                          ) : (
                            sub.payment
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu
                            open={openDropdownId === sub.id}
                            onOpenChange={(open) => setOpenDropdownId(open ? sub.id : null)}
                          >
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost">
                                <MoreHorizontal />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => navigate("/services/photo/" + sub.id)}>
                                <Camera className="mr-2" /> Manage Photo Tasks
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate("/services/task/" + sub.id)}>
                                <List className="mr-2" /> Manage Task List
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem variant="destructive" onClick={() => handleDeleteServiceModal(sub.id)}>
                                <Trash2 className="mr-2" /> Remove Sub Serivce
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
      <AddServiceModal open={openDialog} setOpen={setOpenDialog} onSubmit={handleCreate} />
      <AddSubServiceModal open={openSubDialog} setOpen={setOpenSubDialog} onSubmit={handleCreateSub} />
      <DeleteServiceModal open={delDialog} setOpen={setDelDialog} handleDelete={handleDelete} />
    </MainLayout>
  );
}
